import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeftRight, Ban, CheckCircle2, Loader2, Lock, Pencil, PiggyBank, Plus, Tags, Trash2, TrendingDown, TrendingUp } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import categoryService, { buildCategoryTree } from '@services/categoryService';
import { MOVEMENT_LABELS_PLURAL, type Category, type Movement } from '@/types/finance';
import { formatBRL } from '@/utils/money';
import { ColorDot, PillTabs } from './financeBits';
import { CategoryFormModal } from './CategoryFormModal';

/**
 * Finanças → Categorias: abas Despesas / Receitas / Transferências, árvore de 2 níveis montada
 * no front (`parent_category_id`), contagem de lançamentos, orçamento padrão (despesas, com atalho
 * para a tela de Orçamento), editar / subcategoria / desativar / excluir. Erros 409 (tem
 * lançamentos, subcategorias, regras ou é de sistema) vêm do backend.
 */
export const CategoriesView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [movement, setMovement] = useState<Movement>('expense');
  const [items, setItems] = useState<Category[]>([]);
  const [showDisabled, setShowDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ category?: Category; parent?: Category } | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await categoryService.getAll({ include_disabled: true }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tree = useMemo(() => {
    const visible = showDisabled ? items : items.filter((c) => !c.is_disabled);
    return buildCategoryTree(visible, movement);
  }, [items, movement, showDisabled]);

  const counts = useMemo(() => {
    const out: Record<Movement, number> = { expense: 0, income: 0, transfer: 0 };
    for (const c of items) if (!c.is_disabled) out[c.movement]++;
    return out;
  }, [items]);

  const toggleDisabled = async (c: Category) => {
    setBusy(c.id);
    try {
      await categoryService.update(c.id, {
        name: c.name,
        movement: c.movement,
        parent_category_id: c.parent_category_id ?? null,
        icon: c.icon ?? null,
        color: c.color ?? null,
        sort_order: c.sort_order ?? null,
        is_disabled: !c.is_disabled,
      });
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(deleting.id);
    try {
      await categoryService.remove(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const row = (c: Category, depth: number) => (
    <li key={c.id} className={clsx('flex items-center gap-2 px-3 py-2', depth > 0 && 'pl-9 bg-slate-50/60', c.is_disabled && 'opacity-60')}>
      <ColorDot color={c.color} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-800 flex flex-wrap items-center gap-1.5">
          <span className="[overflow-wrap:anywhere]">{c.name}</span>
          {c.is_system && (
            <span className="badge-neutral inline-flex items-center gap-1" title="Categoria de sistema">
              <Lock size={10} /> sistema
            </span>
          )}
          {c.is_disabled && <span className="badge-neutral">desativada</span>}
        </p>
        <p className="text-small">
          {c.transaction_count === 0 ? 'sem lançamentos' : `${c.transaction_count.toLocaleString('pt-BR')} ${c.transaction_count === 1 ? 'lançamento' : 'lançamentos'}`}
          {c.icon && ` · ícone ${c.icon}`}
          {c.movement === 'expense' && c.default_budget_cents ? (
            <>
              {' · '}
              <Link to="/finance/budget" className="inline-flex items-center gap-1 text-emerald-700 hover:underline" title="Orçamento mensal padrão — abrir Orçamento">
                <PiggyBank size={11} /> orçamento {formatBRL(c.default_budget_cents)}/mês
              </Link>
            </>
          ) : null}
        </p>
      </div>
      {canWrite && (
        <div className="flex items-center">
          {depth === 0 && c.movement !== 'transfer' && (
            <ActionButton icon={<Plus size={15} />} variant="view" tooltip="Nova subcategoria" onClick={() => setForm({ parent: c })} />
          )}
          <ActionButton icon={<Pencil size={15} />} variant="edit" tooltip="Editar" onClick={() => setForm({ category: c })} />
          <ActionButton
            icon={c.is_disabled ? <CheckCircle2 size={15} /> : <Ban size={15} />}
            variant="default"
            tooltip={c.is_disabled ? 'Reativar' : 'Desativar'}
            disabled={busy === c.id}
            onClick={() => toggleDisabled(c)}
          />
          {!c.is_system && <ActionButton icon={<Trash2 size={15} />} variant="delete" tooltip="Excluir" disabled={busy === c.id} onClick={() => setDeleting(c)} />}
        </div>
      )}
    </li>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Tags size={20} className="text-slate-500" /> Categorias
        </h1>
        <div className="grid grid-cols-1 sm:flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />
            Mostrar desativadas
          </label>
          <PermissionGuard permission="financas.write">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({})} disabled={movement === 'transfer'}>
              Nova categoria
            </Button>
          </PermissionGuard>
        </div>
      </div>

      <PillTabs
        tabs={[
          { id: 'expense', label: MOVEMENT_LABELS_PLURAL.expense, icon: <TrendingDown size={15} />, count: counts.expense },
          { id: 'income', label: MOVEMENT_LABELS_PLURAL.income, icon: <TrendingUp size={15} />, count: counts.income },
          { id: 'transfer', label: MOVEMENT_LABELS_PLURAL.transfer, icon: <ArrowLeftRight size={15} />, count: counts.transfer },
        ]}
        active={movement}
        onChange={(id) => setMovement(id as Movement)}
      />

      {movement === 'transfer' && (
        <p className="text-small">
          As categorias de transferência são de sistema e só entram pelas transferências entre contas e pelo pagamento de fatura. Dá para renomear e
          mudar a cor.
        </p>
      )}
      {movement === 'expense' && (
        <p className="text-small">
          O orçamento mensal de cada categoria de despesa é definido em{' '}
          <Link to="/finance/budget" className="text-emerald-700 hover:underline">
            Orçamento
          </Link>
          ; aqui aparece só o valor padrão.
        </p>
      )}

      {loading && items.length === 0 ? (
        <p className="text-center text-slate-400 py-6">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      ) : tree.length === 0 ? (
        <p className="text-center text-slate-500 py-8">Nenhuma categoria de {MOVEMENT_LABELS_PLURAL[movement].toLowerCase()}.</p>
      ) : (
        <ul className={clsx('card divide-y divide-slate-100 overflow-hidden', loading && 'opacity-60')}>
          {tree.map((n) => (
            <React.Fragment key={n.category.id}>
              {row(n.category, 0)}
              {n.children.map((c) => row(c, 1))}
            </React.Fragment>
          ))}
        </ul>
      )}

      {form && (
        <CategoryFormModal
          category={form.category}
          parent={form.parent}
          categories={items}
          movement={movement}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Excluir categoria"
        message={`Excluir "${deleting?.name ?? ''}"? Só é possível quando não há subcategorias, lançamentos nem regras de importação usando a categoria — nesses casos, prefira desativar.`}
        confirmText="Excluir"
        severity="danger"
        loading={busy !== null}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
